import type { GradeCareerQuizMutation } from 'core/graphql';

/** Per-question outcome after grading (spec 0020 Фаза C). */
export interface ICareerQuizQuestionResult {
  correct: boolean;
  /**
   * The revealed answer key — server sends `correctIndexes` ONLY in the submit
   * result (it is `[]` on the pre-submit `quizQuestions`), so the UI can now
   * highlight the right options.
   */
  correctIndexes: number[];
}

/** Normalized grade of a submitted quiz. */
export interface ICareerQuizResult {
  success: boolean;
  score: number;
  maxScore: number;
  /** Outcome keyed by `questionId` for O(1) lookup while rendering feedback. */
  byQuestion: Record<string, ICareerQuizQuestionResult>;
  /** First backend customError, if any (e.g. not-a-member / lesson locked). */
  errorCode: string | null;
  errorMessage: string | null;
}

/**
 * Normalizes the `submitCareerQuiz` payload into a view model.
 *
 * `null` in / `null` out: a transport failure (no payload) is surfaced by the
 * hook as a thrown error, not here. A graded-but-`success:false` payload keeps
 * its `customErrors` so the quiz block can show the server's message.
 */
export const transformCareerQuizResult = (
  data: GradeCareerQuizMutation | undefined | null,
): ICareerQuizResult | null => {
  const payload = data?.submitCareerQuiz;

  if (!payload) {
    return null;
  }

  const firstError = payload.customErrors?.[0] ?? null;

  const byQuestion = (payload.results ?? []).reduce<Record<string, ICareerQuizQuestionResult>>(
    (acc, result) => {
      acc[result.questionId] = {
        correct: result.correct,
        correctIndexes: [...result.correctIndexes],
      };
      return acc;
    },
    {},
  );

  return {
    success: Boolean(payload.success),
    score: payload.score ?? 0,
    maxScore: payload.maxScore ?? 0,
    byQuestion,
    errorCode: firstError?.code ?? null,
    errorMessage: firstError?.message ?? null,
  };
};
